import React, { useEffect, useState } from 'react';
import { UserMenu, Logout, RefreshIconButton } from 'react-admin';
import {
  Box,
  AppBar,
  Toolbar,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  CircularProgress,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Badge as BadgeIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const MyUserMenu = (props: any) => {
  const { dashboard, ...sanitizedProps } = props;

  return (
    <UserMenu {...sanitizedProps}>
      <Logout />
    </UserMenu>
  );
};

const DRAWER_WIDTH = 240;

export const StandaloneLayout = ({ children }: any) => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);

  // Check authentication on component mount
  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    const authenticated = !!token;
    setIsAuthenticated(authenticated);

    // If not authenticated, redirect to login
    if (!authenticated) {
      console.log('[StandaloneLayout] No access token found, redirecting to login');
      navigate('/login', { replace: true });
    }
  }, [navigate]);

  // Show loading state while checking auth
  if (isAuthenticated === null) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // If not authenticated, don't render anything (will redirect above)
  if (!isAuthenticated) {
    return null;
  }

  const menuItems = [
    {
      label: 'Dashboard',
      icon: <DashboardIcon />,
      path: '/standalone-dashboard',
    },
    {
      label: 'My Driver Profile',
      icon: <BadgeIcon />,
      path: '/drivers/profile',
    },
  ];

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', backgroundColor: '#F5F7FA' }}>
      {/* App Bar */}
      <AppBar
        color="primary"
        elevation={0}
        sx={{
          backgroundColor: 'transparent',
          color: '#1C5195',
          position: 'fixed',
          zIndex: (theme) => theme.zIndex.drawer + 1,
          '&.MuiPaper-root': {
            borderBottom: 'none',
            boxShadow: 'none',
          },
        }}
      >
        <Toolbar sx={{ width: '100%', display: 'flex', justifyContent: 'space-between' }}>
          <Box />

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
            }}
          >
            <RefreshIconButton />

            <Box
              sx={{
                width: '1px',
                height: '24px',
                backgroundColor: '#C8D4D2',
                border: '1px solid #C8D4D2',
              }}
            />

            <MyUserMenu />
          </Box>
        </Toolbar>
      </AppBar>

      {/* Side Drawer */}
      <Box
        sx={{
          textTransform: 'capitalize',
          boxShadow: '0 3px 10px rgba(0, 0, 0, 0.1)',
          backgroundColor: '#fff',
          margin: '16px 0 16px 16px',
          borderRadius: '16px',
          position: 'fixed',
          top: 0,
          left: 0,
          zIndex: 1200,
          height: 'calc(100vh - 32px)',
          display: 'flex',
          flexDirection: 'column',
          width: DRAWER_WIDTH,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '32px 16px 16px 16px',
            borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
            marginBottom: '16px',
          }}
        >
          <Box
            component="img"
            src="/logo-dv-full-text.png"
            alt="DV Logo"
            sx={{
              height: 32,
              maxWidth: '80%',
              objectFit: 'contain',
            }}
          />
        </Box>

        <Box sx={{ overflowY: 'auto', flex: 1 }}>
          <List disablePadding>
            {menuItems.map((item) => (
              <ListItem key={item.path} disablePadding>
                <ListItemButton
                  onClick={() => navigate(item.path)}
                  sx={{
                    borderLeft: '5px solid transparent',
                    padding: '10px 16px',
                    color: '#555',
                    borderRadius: 0,
                    minHeight: '48px',
                    width: '100%',
                    '&:hover': {
                      borderLeft: '5px solid #DDD8D6',
                      color: '#1C5195',
                      backgroundColor: 'rgba(221, 216, 214, 0.1)',
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  <ListItemIcon
                    sx={{
                      color: 'inherit',
                      minWidth: '40px',
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    sx={{
                      '& .MuiListItemText-primary': {
                        color: 'inherit',
                        fontWeight: 'inherit',
                      },
                    }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Box>
      </Box>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flex: 1,
          padding: '16px',
          marginLeft: '256px', /* Menu width (240px) + left margin (16px) */
          backgroundColor: '#F5F7FA',
        }}
      >
        <Toolbar />
        {children}
      </Box>
    </Box>
  );
};
